import { useEffect, useState } from 'react';

import type { IQuotePriceCalculationInput, IQuotePriceCalculationResult } from '../../interfaces/quotes';
import { Quotes } from '../../services/api/quotes/quotes';
import { useUser } from '../useUser/useUser';

const DEBOUNCE_TIME = 500;

export const useQuotePriceCalculator = (quoteData: IQuotePriceCalculationInput, skip: boolean = false) => {
  const { brokerId } = useUser();
  const [price, setPrice] = useState<IQuotePriceCalculationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const quoteDataStr = JSON.stringify(quoteData);

  useEffect(() => {
    let active = true;
    let started = false;

    if (skip) {
      return;
    }

    const handler = setTimeout(() => {
      const parsedQuoteData = JSON.parse(quoteDataStr) as IQuotePriceCalculationInput;
      const hasProducts =
        (parsedQuoteData.products.insurance?.length ?? 0) > 0 ||
        (parsedQuoteData.products.wellness?.length ?? 0) > 0 ||
        (parsedQuoteData.products.voluntary?.length ?? 0) > 0;

      if (brokerId && hasProducts) {
        started = true;
        setLoading(true);
        setError(null);
        Quotes.getPriceCalculation(brokerId, parsedQuoteData)
          .then((res) => {
            if (active) setPrice(res);
          })
          .catch((err) => {
            if (active) setError(err);
          })
          .finally(() => {
            if (active) setLoading(false);
          });
      } else {
        setPrice(null);
        setError(null);
        setLoading(false);
      }
    }, DEBOUNCE_TIME);

    return () => {
      active = false;
      clearTimeout(handler);
      if (started) setLoading(false);
    };
  }, [quoteDataStr, brokerId, skip]);

  return { price, loading, error };
};
